import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader, Package } from 'lucide-react';
import { apogeeApiService } from '../../services/apogeeApi';
import type { PackRow } from '../../types/apogee';
import { cn } from '../../utils/cn';
import { packDetailPath } from '../../utils/packRoutes';

export interface ApogeeLinkedPackSummaryProps {
  apogeeId: number;
  className?: string;
  /** Larger card for project header area */
  variant?: 'compact' | 'featured';
}

export function ApogeeLinkedPackSummary({
  apogeeId,
  className,
  variant = 'compact',
}: ApogeeLinkedPackSummaryProps) {
  const [pack, setPack] = useState<PackRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    apogeeApiService
      .getPackByApogeeId(apogeeId)
      .then((p) => {
        if (!cancelled) setPack(p);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apogeeId]);

  if (loading) {
    return (
      <div className={cn('flex items-center gap-2 text-glass-400 text-sm', className)}>
        <Loader className="w-4 h-4 animate-spin shrink-0" />
        Loading linked modpack…
      </div>
    );
  }

  if (failed || !pack) {
    return (
      <div className={cn('text-sm text-amber-400/90', className)}>
        Linked Apogee pack (id {apogeeId}) — details unavailable.
      </div>
    );
  }

  const isFeatured = variant === 'featured';

  return (
    <div
      className={cn(
        'subcard',
        isFeatured ? 'p-4' : 'p-3',
        className
      )}
    >
      <p className="eyebrow mb-2">
        Linked modpack
      </p>
      <div className={cn('flex gap-3', isFeatured && 'sm:gap-4')}>
        <div
          className={cn(
            'rounded-md overflow-hidden border border-glass-200/15 bg-dark-900/50 shrink-0',
            isFeatured ? 'w-16 h-16 sm:w-20 sm:h-20' : 'w-12 h-12'
          )}
        >
          {pack.thumbnail_url ? (
            <img src={pack.thumbnail_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className={cn('text-glass-500', isFeatured ? 'w-8 h-8' : 'w-5 h-5')} />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h4 className={cn('font-semibold text-white leading-tight truncate', isFeatured ? 'text-base' : 'text-sm')}>
            {pack.name}
          </h4>
          <p className="text-[11px] text-glass-500 font-mono truncate mt-0.5">{pack.slug}</p>
          {pack.summary && (
            <p className={cn('text-glass-400 mt-1.5 line-clamp-2', isFeatured ? 'text-sm' : 'text-xs')}>
              {pack.summary}
            </p>
          )}
          <Link
            to={packDetailPath(pack)}
            className="link inline-flex items-center gap-1.5 mt-2 text-xs font-medium"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open in modpack browser
          </Link>
        </div>
      </div>
    </div>
  );
}
